import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

import { getPageKeyWithVersion } from "features/ui/Filters/utils";

const pendingUrlUpdates = new Map<string, string | undefined>();
const FLUSH_TIMEOUT_MS = 50;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleUrlUpdate = (key: string, value: string | undefined) => {
  pendingUrlUpdates.set(key, value);
  if (flushTimer !== null) clearTimeout(flushTimer);

  flushTimer = setTimeout(() => {
    // In tests the debounce can outlive the jsdom environment; firing then
    // would crash on the missing window.
    if (typeof window === "undefined") return;

    const currentQuery = new URLSearchParams(window.location.search);
    pendingUrlUpdates.forEach((v, k) =>
      v === undefined ? currentQuery.delete(k) : currentQuery.set(k, v)
    );
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${currentQuery.toString()}`
    );
    pendingUrlUpdates.clear();
    flushTimer = null;
  }, FLUSH_TIMEOUT_MS);
};

const loadLocalStorageJson = (key: string) => {
  const state = localStorage.getItem(key);
  // "undefined" comes from previously persisted undefined values
  if (state && state !== "undefined") {
    try {
      return JSON.parse(state);
    } catch (error) {
      Sentry.captureException(error);
      console.error(
        "Could not parse filter state from local storage",
        error,
        state
      );

      return undefined;
    }
  }

  return undefined;
};

// This hook is used to manage the state of a single JSON object
export const useJsonState = <T = any>({
  initialState,
  pageKey,
}: {
  initialState: T;
  pageKey: string;
}) => {
  const pageKeyWithVersion = getPageKeyWithVersion(pageKey);

  const persistJson = (value: T) => {
    // JSON.stringify(undefined) returns undefined, which setItem would
    // coerce to the unparseable string "undefined"
    const serialized: string | undefined = JSON.stringify(value);
    if (serialized === undefined) {
      localStorage.removeItem(pageKeyWithVersion);
    } else {
      localStorage.setItem(pageKeyWithVersion, serialized);
    }

    scheduleUrlUpdate(pageKeyWithVersion, serialized);
  };

  const getPersistedJson = (): any | undefined => {
    // First check URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const urlValue = urlParams.get(pageKeyWithVersion);

    if (urlValue && urlValue !== "undefined") {
      try {
        return JSON.parse(urlValue);
      } catch (error) {
        Sentry.captureException(error);
        console.error(
          "Could not parse filter state from query string",
          error,
          urlValue
        );

        return initialState;
      }
    }

    const state = loadLocalStorageJson(pageKeyWithVersion);

    if (!state) return initialState;

    return state;
  };

  const [json, setJson] = useState<T>(getPersistedJson);

  // persist initial state to URL so it's always reflected in the query string
  useEffect(() => {
    persistJson(json);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setJsonAndPersist = (value: any) => {
    setJson(value);
    persistJson(value);
  };

  return {
    json,
    setJson: setJsonAndPersist,
  };
};
